import { Link } from '../../primitives/Link/Link';
import { useState } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Dropdown, type DropdownItem } from '../Dropdown/Dropdown';
import styles from './ChatListItem.module.css';

/**
 * Figma: `Chats` (2059:26254) — one row of DeepDive AI's chat history: the
 * chat's title on the left and, on the right, when it was started. Hovering
 * swaps the time for a `⋯` `Dropdown` (Figma `State=Hover`), and opening that
 * menu is `State=Selected`: the same grey surface with the menu drawn open.
 *
 * Named `ChatListItem` rather than the set's `Chats`, which reads as the list
 * and not as one row of it — flagged in plan.md §7 so the two names can be
 * brought together in whichever direction the designer prefers.
 *
 * `Starred?=False|True` (2026-09-22) adds a Ghost Grey star toggle before the
 * menu. A starred row keeps its filled star visible at rest, beside the time;
 * an unstarred one shows the outline only on hover or focus. The toggle is a
 * real `aria-pressed` button — Figma draws two glyphs and says nothing about
 * what announces the state.
 *
 * Renders an `<li>`, so the caller supplies the `<ul>`; the rows are a list
 * for assistive tech as well as to the eye.
 */
export type ChatListItemState = 'default' | 'hover' | 'selected';

export type ChatListItemProps = {
  /** Figma's left `Text` — the chat's title. */
  title: string;
  /** Figma's right `Text` — "1 hour ago". */
  timeLabel: string;
  /** Where the row goes. The whole row is the link, as the sidebar's rows are. */
  href: string;
  /** The `⋯` menu's rows — Figma draws `Rename` and `Delete`. */
  menuItems: DropdownItem[];
  onMenuSelect?: (id: string) => void;
  /** Figma `Starred?`. */
  starred?: boolean;
  /** Receives the new value. Omit to render the star without a toggle behind it. */
  onStarredChange?: (starred: boolean) => void;
  /** Force a state for documentation and visual regression only. */
  state?: ChatListItemState;
  className?: string;
};

export function ChatListItem({
  title,
  timeLabel,
  href,
  menuItems,
  onMenuSelect,
  starred = false,
  onStarredChange,
  state,
  className,
}: ChatListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const forced = state !== undefined;

  const classes = [
    styles.row,
    state === 'hover' ? styles.hover : null,
    state === 'selected' || menuOpen ? styles.selected : null,
    starred ? styles.starred : null,
    // Live hover only when no state is forced.
    forced ? null : styles.interactive,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      {/*
       * A real `<a>`, stretched over the row with a pseudo-element, so the
       * browser navigates and `useHashRoute` reports it. The menu sits above
       * the stretched area (see `.meta` in the stylesheet) so it stays
       * clickable. Not in Figma, which draws styled text and says nothing about
       * what the row does.
       */}
      <Link className={styles.title} href={href}>
        {title}
      </Link>

      <span className={styles.meta}>
        {/* Figma's star `Buttons/Button` — Ghost Grey, icon only, `Starred` or `Unstarred`. */}
        <span className={styles.star}>
          <Button
            hierarchy="ghostGrey"
            aria-label={starred ? `Unstar ${title}` : `Star ${title}`}
            title={starred ? 'Unstar' : 'Star'}
            aria-pressed={starred}
            onClick={() => onStarredChange?.(!starred)}
            iconLeading={
              <span className={styles.glyph}>
                <Icon
                  name={starred ? 'starred' : 'unstarred'}
                  className={starred ? styles.glyphStarred : undefined}
                />
              </span>
            }
          />
        </span>
        <span className={styles.time}>{timeLabel}</span>
        {/* Figma `Actions  Row` — the `Dropdown / Type=Button` with its `more` glyph. */}
        <span className={styles.actions}>
          <Dropdown
            items={menuItems}
            aria-label={`Actions for ${title}`}
            open={state === 'selected' ? true : forced ? false : menuOpen}
            onOpenChange={setMenuOpen}
            onSelect={onMenuSelect}
          />
        </span>
      </span>
    </li>
  );
}
